import { RefreshCw, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isSupportedLocale } from '@erp/shared';
import { LanguageDropdown } from '@/components/common/language-dropdown';
import { ThemeToggle } from '@/components/common/theme-toggle';
import { Button } from '@/components/ui/button';
import type { ApiError } from '@/lib/api-client';
import { changeLocale } from '@/lib/i18n';

const COPY = {
  de: { title: 'Wartungsarbeiten', retry: 'Erneut prüfen' },
  en: { title: 'Maintenance in progress', retry: 'Check again' },
  tr: { title: 'Bakım çalışması', retry: 'Tekrar kontrol et' },
  fr: { title: 'Maintenance en cours', retry: 'Vérifier à nouveau' },
  es: { title: 'Mantenimiento en curso', retry: 'Comprobar de nuevo' },
} as const;

function messageFor(error: ApiError, locale: string): string {
  const normalized = locale.split('-')[0] ?? '';
  const language = isSupportedLocale(normalized) ? normalized : 'en';
  return (
    error.details?.find((detail) => detail.path === `messages.${language}`)?.message ??
    error.message
  );
}

export function MaintenancePage({ error }: { error: ApiError }): JSX.Element {
  const { i18n } = useTranslation();
  const normalized = i18n.language.split('-')[0] ?? '';
  const locale = isSupportedLocale(normalized) ? normalized : 'en';
  const copy = COPY[locale];

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="absolute right-4 top-4 flex gap-2">
        <LanguageDropdown locale={locale} onLocaleChange={changeLocale} />
        <ThemeToggle />
      </div>
      <section
        aria-labelledby="maintenance-title"
        className="w-full max-w-xl rounded-xl border bg-card p-8 text-center shadow-sm"
      >
        <Wrench aria-hidden="true" className="mx-auto mb-5 size-12 text-primary" />
        <h1 id="maintenance-title" className="text-2xl font-semibold">
          {copy.title}
        </h1>
        <p className="mt-4 text-muted-foreground" role="status">
          {messageFor(error, locale)}
        </p>
        <Button className="mt-6" type="button" onClick={() => window.location.reload()}>
          <RefreshCw aria-hidden="true" className="mr-2 size-4" />
          {copy.retry}
        </Button>
      </section>
    </main>
  );
}
